import { useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { login } from '@/mocks/api';
import { useMutation } from '@/hooks/useAsync';
import { useSession } from '@/state/session';
import { errorMessage, isMockApiError } from '@/mocks/errors';
import { Notice } from '@/components/feedback';
import { BrandMark } from '@/components/mobile-shell/DesktopStage';

/**
 * Mobile login.
 *
 * TRACE:XS-SMEM-TT-R005-A1 — one front door: digital banking and the card /
 * expense area share this single sign-in. There is no second login for cards,
 * and the white-label admin portal is a separate locked system that is not
 * reachable from here.
 * TRACE:XS-SMEM-WR-016 — documented SCA challenge method follows login.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const beginSca = useSession((s) => s.beginSca);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const emailId = useId();
  const passwordId = useId();
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);
  const passwordRef = useRef<HTMLInputElement | null>(null);

  const state = location.state as { reason?: string; returnTo?: string } | null;
  const sessionExpiredNotice = state?.reason === 'session-expired';

  const mutation = useMutation(async () => login(email, password));

  const [summaryError, setSummaryError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFieldErrors({});
    setSummaryError(null);

    const outcome = await mutation.run();
    if (!outcome.ok) {
      if (outcome.error === null) return; // duplicate submit, already in flight
      const err = outcome.error;
      // Focus the first invalid field, keeping entered data intact.
      if (isMockApiError(err) && err.fieldErrors) {
        setFieldErrors(err.fieldErrors);
        if (err.fieldErrors['email']) emailRef.current?.focus();
        else if (err.fieldErrors['password']) passwordRef.current?.focus();
      } else {
        setSummaryError(errorMessage(err));
        // Focus moves to the summary once it has rendered.
        requestAnimationFrame(() => summaryRef.current?.focus());
      }
      return;
    }
    beginSca(outcome.value.data.user.id, outcome.value.data.challengeId, state?.returnTo ?? null);
    navigate('/mobile/sca');
  };

  const topError = summaryError;

  return (
    <div className="tm-stack" style={{ paddingBlockStart: 32 }}>
      <div className="tm-login-brand">
        <BrandMark className="tm-login-brand__mark" />
        <div>
          <p className="tm-eyebrow">Tribe Bank</p>
          <h1 className="tm-page-title" style={{ marginBlockStart: 2 }}>
            Sign in
          </h1>
        </div>
      </div>
      <p className="tm-muted" style={{ marginBlockStart: -4 }}>
        One sign-in for your banking, cards and expenses.
      </p>

      {sessionExpiredNotice ? (
        <Notice tone="info" title="You were signed out">
          Your session timed out for security. Sign in again to continue.
        </Notice>
      ) : null}

      {topError ? (
        <div
          className="tm-error-summary"
          role="alert"
          tabIndex={-1}
          ref={summaryRef}
          aria-label="There is a problem"
        >
          <strong>There is a problem</strong>
          <p style={{ margin: '4px 0 0' }}>{topError}</p>
        </div>
      ) : null}

      <form className="tm-stack" onSubmit={(e) => void submit(e)} noValidate>
        <div className="tm-field">
          <label className="tm-label" htmlFor={emailId}>
            Email address
          </label>
          <input
            id={emailId}
            ref={emailRef}
            className="tm-input"
            type="email"
            inputMode="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={fieldErrors['email'] ? true : undefined}
            aria-describedby={fieldErrors['email'] ? `${emailId}-error` : undefined}
          />
          <div className="tm-field__error-slot">
            {fieldErrors['email'] ? (
              <span className="tm-field__error" id={`${emailId}-error`}>
                {fieldErrors['email']}
              </span>
            ) : null}
          </div>
        </div>

        <div className="tm-field">
          <label className="tm-label" htmlFor={passwordId}>
            Password
          </label>
          <input
            id={passwordId}
            ref={passwordRef}
            className="tm-input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            aria-invalid={fieldErrors['password'] ? true : undefined}
            aria-describedby={fieldErrors['password'] ? `${passwordId}-error` : undefined}
          />
          <div className="tm-field__error-slot">
            {fieldErrors['password'] ? (
              <span className="tm-field__error" id={`${passwordId}-error`}>
                {fieldErrors['password']}
              </span>
            ) : null}
          </div>
        </div>

        <button type="submit" className="tm-btn tm-btn--block" disabled={mutation.pending}>
          {mutation.pending ? 'Signing in…' : 'Continue'}
        </button>
      </form>

      {/* One-tap sign-in. Selecting a person fills their credentials, so
          Continue works immediately and the security code is pre-entered on the
          next screen. */}
      <section aria-labelledby="quick-signin">
        <h2 className="tm-eyebrow" id="quick-signin" style={{ marginBlockEnd: 8 }}>
          Sign in as
        </h2>
        <div className="tm-persona-grid">
          {QUICK_SIGN_IN.map((persona) => (
            <button
              key={persona.email}
              type="button"
              className="tm-persona"
              onClick={() => {
                setEmail(persona.email);
                setPassword(DEMO_PASSWORD);
                setFieldErrors({});
                setSummaryError(null);
              }}
              aria-pressed={email === persona.email}
            >
              <span className="tm-persona__avatar" aria-hidden="true">
                {persona.initials}
              </span>
              <span className="tm-persona__body">
                <span className="tm-persona__name">{persona.name}</span>
                <span className="tm-persona__role">{persona.role}</span>
              </span>
            </button>
          ))}
        </div>
      </section>

      <p className="tm-hint" style={{ textAlign: 'center' }}>
        Protected by two-factor authentication.
      </p>
    </div>
  );
}

/** Shared password for every seeded account. */
export const DEMO_PASSWORD = 'demo';

const QUICK_SIGN_IN = [
  { name: 'Priya Raman', role: 'Business owner', email: 'priya@northgate.example', initials: 'PR' },
  { name: 'Daniel Okafor', role: 'Finance', email: 'daniel@northgate.example', initials: 'DO' },
  { name: 'Helena Vasquez', role: 'Approver', email: 'helena@northgate.example', initials: 'HV' },
  { name: 'Tom Whitfield', role: 'Cardholder', email: 'tom@northgate.example', initials: 'TW' },
  { name: 'Grace Lindholm', role: 'Read-only', email: 'grace@ledgerworks.example', initials: 'GL' },
] as const;
